import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ProposalModal = ({ isOpen, onClose, influencerName, selectedPackage }) => {
  const [formData, setFormData] = useState({
    campaignName: '',
    budget: selectedPackage?.price || '',
    contentType: selectedPackage?.id || '',
    deadline: '',
    description: '',
    musicFile: null
  });

  const [errors, setErrors] = useState({});

  const contentTypeOptions = [
    { value: 'video', label: 'Video İçerik' },
    { value: 'story', label: 'Story Paylaşımı' },
    { value: 'post', label: 'Feed Gönderisi' },
    { value: 'reel', label: 'Reels/Shorts' },
    { value: 'custom', label: 'Özel Paket' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.size > 50 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, musicFile: 'Dosya boyutu 50MB\'dan küçük olmalıdır' }));
        return;
      }
      setFormData(prev => ({ ...prev, musicFile: file }));
      setErrors(prev => ({ ...prev, musicFile: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.campaignName?.trim()) {
      newErrors.campaignName = 'Kampanya adı gereklidir';
    }

    if (!formData?.budget || formData?.budget <= 0) {
      newErrors.budget = 'Geçerli bir bütçe giriniz';
    }

    if (!formData?.contentType) {
      newErrors.contentType = 'İçerik türü seçiniz';
    }

    if (!formData?.deadline) {
      newErrors.deadline = 'Teslim tarihi seçiniz';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = 'Kampanya açıklaması gereklidir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      console.log('Teklif gönderiliyor:', formData);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-[2000] flex items-center justify-center p-4">
      <div className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Kampanya Teklifi Gönder</h2>
              <p className="text-sm text-muted-foreground mt-1">{influencerName} için teklif oluşturun</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-smooth"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="Kampanya Adı"
            type="text"
            placeholder="Örn: Yeni Single Tanıtımı"
            value={formData?.campaignName}
            onChange={(e) => handleInputChange('campaignName', e?.target?.value)}
            error={errors?.campaignName}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Bütçe (₺)"
              type="number"
              placeholder="0"
              value={formData?.budget}
              onChange={(e) => handleInputChange('budget', e?.target?.value)}
              error={errors?.budget}
              required
            />

            <Input
              label="Teslim Tarihi"
              type="date"
              value={formData?.deadline}
              onChange={(e) => handleInputChange('deadline', e?.target?.value)}
              error={errors?.deadline}
              required
            />
          </div>

          <Select
            label="İçerik Türü"
            options={contentTypeOptions}
            value={formData?.contentType}
            onChange={(value) => handleInputChange('contentType', value)}
            error={errors?.contentType}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Kampanya Açıklaması *
            </label>
            <textarea
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
              placeholder="Kampanya detaylarını, hedef kitlenizi ve beklentilerinizi açıklayın..."
              rows={5}
              className={`
                w-full px-3 py-2 bg-background border rounded-md text-foreground
                focus:outline-none focus:ring-2 focus:ring-ring resize-none
                ${errors?.description ? 'border-error' : 'border-input'}
              `}
            />
            {errors?.description && (
              <p className="text-sm text-error mt-1">{errors?.description}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Müzik Dosyası (Opsiyonel)
            </label>
            <div className="relative">
              <input
                type="file"
                accept="audio/*"
                onChange={handleFileChange}
                className="hidden"
                id="music-file"
              />
              <label
                htmlFor="music-file"
                className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted transition-smooth"
              >
                <Icon name="Upload" size={20} color="var(--color-muted-foreground)" />
                <span className="text-sm text-muted-foreground">
                  {formData?.musicFile ? formData?.musicFile?.name : 'Dosya seçmek için tıklayın'}
                </span>
              </label>
            </div>
            {errors?.musicFile && (
              <p className="text-sm text-error mt-1">{errors?.musicFile}</p>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Maksimum dosya boyutu: 50MB. Desteklenen formatlar: MP3, WAV, FLAC
            </p>
          </div>

          <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Teklif Süreci</p>
                <p className="text-sm text-muted-foreground">
                  Teklifiniz influencer tarafından incelenecek ve 48 saat içinde yanıt verilecektir. 
                  Onaylandıktan sonra ödeme işlemi başlatılacaktır.
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              fullWidth
              onClick={onClose}
            >
              İptal
            </Button>
            <Button
              type="submit"
              variant="default"
              fullWidth
              iconName="Send"
              iconPosition="left"
            >
              Teklif Gönder
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProposalModal;